import * as React from 'react';
import { useEffect, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Permissions } from "../../../../helpers/constants";
import { getRouteUrl } from "../../../../helpers/getRouteUrl";
import useMenuAction from "../../../../hooks/menu/useMenuAction";
import useMenuData from "../../../../hooks/menu/useMenuData";
import useUserData from "../../../../hooks/user/useUserData";
import { NAVIGATION } from "../../../../locales/components/namespaces";
import { useHasPermission } from "../../../../modules/permission/hooks/useHasPermission";
import FaqsView from "../../../../pages/faqs/faqsView";
import { ROUTE_PAGE_EXHIBITION } from "../../../../routers/routes";
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from "../../../../themes/config";
import useConfig from "../../../../themes/hooks/useConfig";
import NavGroup from './navGroup';
import NavItem from "./navItem";

const Navigation = () => {
	const {id} = useParams() // exhibition id
	const isExhibitor = useHasPermission([Permissions.BeExhibitor], { shouldView: true })
	const { i18n, t } = useTranslation(NAVIGATION)
	const { language } = useUserData()
	const { drawerOpen, selectedLevel, selectedItems, menuItems } = useMenuData()
	const { setSelectedItems, setSelectedLevel, setDrawerOpen } = useMenuAction()
	const theme = useTheme();
	const { menuOrientation } = useConfig();
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	
	useEffect(() => {
		i18n.changeLanguage(language)
	}, [i18n, language])
	
	const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;
	
	const navGroups = useMemo(() => {
		const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
		let lastItemIndex = menuItems.items.length - 1;
		let remItems = [];
		let lastItemId;
		//  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
		// item, and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems
		if (lastItem && lastItem < menuItems.items.length) {
			lastItemId = menuItems.items[lastItem - 1].id;
			lastItemIndex = lastItem - 1;
			remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
				title: item.title,
				elements: item.children,
				icon: item.icon
			}));
		}
		
		return (
			menuItems.items.slice(0, lastItemIndex + 1).map((item) => {
				switch (item.type) {
					case 'group':
						return (
							<NavGroup
								key={item.id}
								setSelectedItems={setSelectedItems}
								setSelectedLevel={setSelectedLevel}
								selectedLevel={selectedLevel}
								selectedItems={selectedItems}
								lastItem={lastItem}
								remItems={remItems}
								lastItemId={lastItemId}
								item={item}
							/>
						);
					default:
						return (
							<Typography key={item.id} variant="h6" color="error" align="center">
								Fix - Navigation Group
							</Typography>
						);
				}
			})
		)
	}, [isHorizontal, menuItems.items, selectedItems, selectedLevel, setSelectedItems, setSelectedLevel])
	return (
		<Box>
			<Box
				sx={{
					pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
					'& > ul:first-of-type': { mt: 0 },
					display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block',
					p: drawerOpen ? '2rem' : 2
				}}
			>
				{isExhibitor && (
					<>
						<NavItem
							item={{
								url: getRouteUrl(ROUTE_PAGE_EXHIBITION, { id }),
								title: <strong>{t('Home')}</strong>,
								icon: 'HomeIcon',
								hoverMessage: t('Home'),
								id: 'home',
								type: 'item',
								breadcrumbs: false,
							}}
							level={1}
						/>
						{drawerOpen ? (
							<FaqsView
								exhibitionId={id}
							/>
						) : (
							<NavItem
								item={{
									url: '',
									title: <strong>{t('General Info')}</strong>,
									icon: 'InfoIcon',
									hoverMessage: t('General Info'),
									id: '',
									type: 'item',
									breadcrumbs: false,
								}}
								onClickAction={(event) => {
									setDrawerOpen(true)
									// setSelectedItems('')
								}}
								level={1}
								isAction
							/>
						)}
					</>
				)}
				{navGroups}
			</Box>
		</Box>
	);
};

export default Navigation;
